// Import Log Service - Track and export import operations

import { IMPORT_ACTION } from './importConstants.js';

/**
 * ImportLogService class
 * Manages logging of import operations with export capabilities
 */
class ImportLogService {
  constructor() {
    this.logs = [];
    this.sessionStartTime = null;
    this.sessionEndTime = null;
    this.summary = {
      created: 0,
      updated: 0,
      skipped: 0,
      failed: 0
    };
  }

  /**
   * Start logging session
   */
  start() {
    this.logs = [];
    this.sessionStartTime = new Date();
    this.sessionEndTime = null;
    this.summary = {
      created: 0,
      updated: 0,
      skipped: 0,
      failed: 0
    };
  }

  /**
   * End logging session
   */
  end() {
    this.sessionEndTime = new Date();
  }

  /**
   * Log a resource action
   */
  logResourceAction(resourceType, resourceName, action, details = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      resourceType: resourceType,
      resourceName: resourceName,
      action: action, // create, update, skip, error
      ...details
    };

    this.logs.push(logEntry);

    // Update summary
    switch (action) {
      case IMPORT_ACTION.CREATE:
        this.summary.created++;
        break;
      case IMPORT_ACTION.UPDATE:
        this.summary.updated++;
        break;
      case IMPORT_ACTION.SKIP:
        this.summary.skipped++;
        break;
      case IMPORT_ACTION.ERROR:
        this.summary.failed++;
        break;
      default:
        break;
    }

    return logEntry;
  }

  /**
   * Get all logs
   */
  getLogs() {
    return this.logs;
  }

  /**
   * Get summary statistics
   */
  getSummary() {
    return {
      ...this.summary,
      total: this.logs.length,
      duration: this.sessionEndTime && this.sessionStartTime
        ? this.sessionEndTime - this.sessionStartTime
        : null,
      startTime: this.sessionStartTime,
      endTime: this.sessionEndTime
    };
  }

  /**
   * Get logs by action type
   */
  getLogsByAction(action) {
    return this.logs.filter(log => log.action === action);
  }

  /**
   * Get logs by resource type
   */
  getLogsByResourceType(resourceType) {
    return this.logs.filter(log => log.resourceType === resourceType);
  }

  /**
   * Get failed logs only
   */
  getFailedLogs() {
    return this.getLogsByAction(IMPORT_ACTION.ERROR);
  }

  /**
   * Export logs as JSON
   */
  exportAsJson() {
    const exportData = {
      sessionInfo: {
        startTime: this.sessionStartTime?.toISOString(),
        endTime: this.sessionEndTime?.toISOString(),
        duration: this.getSummary().duration
      },
      summary: this.summary,
      logs: this.logs
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Export logs as human-readable text
   */
  exportAsText() {
    const lines = [];

    // Header
    lines.push('AWS Connect Import Log');
    lines.push('='.repeat(50));
    lines.push('');

    // Session info
    if (this.sessionStartTime) {
      lines.push(`Start Time: ${this.sessionStartTime.toLocaleString()}`);
    }
    if (this.sessionEndTime) {
      lines.push(`End Time: ${this.sessionEndTime.toLocaleString()}`);
      const duration = Math.round((this.sessionEndTime - this.sessionStartTime) / 1000);
      lines.push(`Duration: ${duration} seconds`);
    }
    lines.push('');

    // Summary
    lines.push('Summary');
    lines.push('-'.repeat(50));
    lines.push(`Total Operations: ${this.logs.length}`);
    lines.push(`Created: ${this.summary.created}`);
    lines.push(`Updated: ${this.summary.updated}`);
    lines.push(`Skipped: ${this.summary.skipped}`);
    lines.push(`Failed: ${this.summary.failed}`);
    lines.push('');

    // Detailed logs
    lines.push('Detailed Log');
    lines.push('-'.repeat(50));

    this.logs.forEach((log, index) => {
      const time = new Date(log.timestamp).toLocaleTimeString();
      const action = log.action.toUpperCase();
      const status = action === 'ERROR' ? '❌' : action === 'CREATE' ? '✅' : action === 'UPDATE' ? '🔄' : '⏭️';

      lines.push(`${index + 1}. [${time}] ${status} ${action}`);
      lines.push(`   Type: ${log.resourceType}`);
      lines.push(`   Name: ${log.resourceName}`);

      if (log.id) {
        lines.push(`   ID: ${log.id}`);
      }

      if (log.error) {
        lines.push(`   Error: ${log.error}`);
      }

      if (log.reason) {
        lines.push(`   Reason: ${log.reason}`);
      }

      lines.push('');
    });

    return lines.join('\n');
  }

  /**
   * Export logs as CSV
   */
  exportAsCsv() {
    const lines = [];

    // Header
    lines.push('Timestamp,Resource Type,Resource Name,Action,ID,Error,Reason');

    // Data rows
    this.logs.forEach(log => {
      const row = [
        log.timestamp,
        log.resourceType,
        `"${log.resourceName}"`, // Quote to handle commas in names
        log.action,
        log.id || '',
        log.error ? `"${log.error.replace(/"/g, '""')}"` : '', // Escape quotes
        log.reason ? `"${log.reason.replace(/"/g, '""')}"` : ''
      ];
      lines.push(row.join(','));
    });

    return lines.join('\n');
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = [];
    this.sessionStartTime = null;
    this.sessionEndTime = null;
    this.summary = {
      created: 0,
      updated: 0,
      skipped: 0,
      failed: 0
    };
  }

  /**
   * Download log file
   */
  downloadLog(format = 'json', filename = 'import-log') {
    let content;
    let mimeType;
    let extension;

    switch (format) {
      case 'json':
        content = this.exportAsJson();
        mimeType = 'application/json';
        extension = 'json';
        break;
      case 'text':
        content = this.exportAsText();
        mimeType = 'text/plain';
        extension = 'txt';
        break;
      case 'csv':
        content = this.exportAsCsv();
        mimeType = 'text/csv';
        extension = 'csv';
        break;
      default:
        throw new Error(`Unsupported format: ${format}`);
    }

    // Create blob and trigger download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}-${new Date().toISOString().split('T')[0]}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export default ImportLogService;
